import { h, Component } from 'preact';
import { Router } from 'preact-router';
import Header from './header';
import Content from './content';
import Footer from './footer';
import GlobalAccess from '../services/globalAccess';
import { withTranslation } from 'react-i18next';
import  '../services/i18n';
import { getToken } from '../services/tokenStorage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Configuration from '../constant/configuration';
import { AppConstant } from '../constant/AppConstant';
import { ADD_DEVICE_RETURN_TO_KEY, SUBSCRIBE_RETURN_TO_KEY } from '../constant/utils';
import { getCconfig } from '../services/cconfigService';
import { initVersionCheck, stopVersionCheck, adoptNewBuild } from '../services/versionCheckService';
import { showUpdatePrompt } from '../services/toastService';
 class App extends Component {
	constructor(props) {
		super(props);
		this.refreshInterval = null; // Store interval ID for cleanup
		this.pollCount = 0; // Track number of polls for exponential backoff
		if (typeof window !== "undefined") {
			this.state = {
				ktok: getToken(), // Use encrypted token retrieval
				headerName: "Sites",
				backlink: "",
				devices: {},
				devicesLoaded: false,
				servings: {},
				pubkeys: {},
				refresh: false,
				theme: 'dark',
				lang:"",
				isDemo: 0,
				untappd_token: "",
				bizDayStart: "00:00",
				enabledServingSizes: Configuration.defaultEnabledServingSizes
			}
		}
	}
	applyTheme = (theme) => {
		if (theme === 'light') {
			document.documentElement.setAttribute('data-theme', 'light');
		} else {
			document.documentElement.removeAttribute('data-theme');
		}
		this.setState({ theme });
	}
	componentDidMount() {
		const self = this;
		const {i18n, t}=this.props
		i18n.changeLanguage(navigator.language)
		self.setState({lang:i18n.language})

		// Detect new front-end deployments and prompt the user to reload.
		// Translated lazily inside the callback so the prompt respects whatever
		// language is active when an update is actually detected.
		initVersionCheck(() => {
			showUpdatePrompt({
				message: t(AppConstant.APP_UPDATE_AVAILABLE),
				actionLabel: t(AppConstant.APP_UPDATE_RELOAD),
				// NOT a bare location.reload(): that races the new service
				// worker's install and lands back on the stale precached shell,
				// re-prompting forever (2026-08-13 toast storm). adoptNewBuild
				// waits for the fresh worker to activate before navigating.
				onAction: () => adoptNewBuild(),
			});
		});

		// Note: We do NOT clear credentials from URL here
		// The login component will clear them AFTER processing the credentials
		// This allows demo mode (/?creds=demo@kegtron.com:demo) to work

		if (!getToken()) {
			// Login page always dark — no user preference loaded
			document.documentElement.removeAttribute('data-theme');
			// If no token, ensure we're on login page
			// Only route if we're not already on "/" to preserve query parameters.
			// Exempt the public "Get the app" landing (/app/*): it is the
			// in-browser target for inbound App/Universal Links when the app is
			// not installed, so it must render for logged-out visitors.
			const isPublicAppLanding = window.location.pathname.indexOf("/app") === 0;
			if (window.location.pathname !== "/" && !isPublicAppLanding) {
				// Preserve a pending Add Device claim across the login redirect
				// so the user returns to it after authenticating (Pro Wi-Fi
				// Provisioning hand-off; this guard runs before the route mounts).
				if (window.location.pathname.indexOf("/add-device") === 0 &&
					new URLSearchParams(window.location.search).get("claim")) {
					try {
						localStorage.setItem(
							ADD_DEVICE_RETURN_TO_KEY,
							`${window.location.pathname}${window.location.search}`
						);
					} catch (e) {
						// localStorage unavailable — fall through to login.
					}
				}
				// Preserve a pending Subscribe intent across the login redirect the
				// same way, for a cold load directly on /subscribe (e.g. a
				// bookmarked or shared "Add ports" link) while logged out. The
				// Subscribe route also stashes this itself on mount, but this
				// earlier guard runs before that route ever mounts.
				if (window.location.pathname === "/subscribe") {
					try {
						localStorage.setItem(
							SUBSCRIBE_RETURN_TO_KEY,
							`${window.location.pathname}${window.location.search}`
						);
					} catch (e) {
						// localStorage unavailable — fall through to login.
					}
				}
				Router.route("/");
			}
			// If already on "/", let the Login component handle the URL parameters
		} else {
			getCconfig().then(cconfig => {
				self.applyTheme(cconfig.theme_pref || 'dark');
			}).catch(() => {});
			GlobalAccess.getDevices(self);
			this.startSmartPolling();
		}
	}
	componentWillUnmount() {
		if (this.refreshInterval) {
			clearTimeout(this.refreshInterval);
			this.refreshInterval = null;
		}
		stopVersionCheck();
	}
	startSmartPolling = () => {
		// Clear any existing timeout
		if (this.refreshInterval) {
			clearTimeout(this.refreshInterval);
		}

		// Reset poll count
		this.pollCount = 0;

		// Start the polling loop
		this.scheduleNextPoll();
	}
	scheduleNextPoll = () => {
		const self = this;
		// Clear any existing timeout
		if (this.refreshInterval) {
			clearTimeout(this.refreshInterval);
		}

		// Schedule next poll with current interval
		this.refreshInterval = setTimeout(() => {
			if (self.state.refresh) {
				GlobalAccess.getDevices(self);
				self.pollCount++;
			}
			// Schedule next poll (this creates exponential backoff)
			self.scheduleNextPoll();
		}, this.getPollingInterval());
	}
	getPollingInterval = () => {
		// Exponential backoff: 3s -> 5s -> 10s -> 20s -> 30s (max)
		// Polls 0-5: 3 seconds (first 15 seconds)
		if (this.pollCount < 5) return 3000;
		// Polls 6-10: 5 seconds (next 25 seconds)
		if (this.pollCount < 10) return 5000;
		// Polls 11-15: 10 seconds (next 50 seconds)
		if (this.pollCount < 15) return 10000;
		// Polls 16-20: 20 seconds (next 100 seconds)
		if (this.pollCount < 20) return 20000;
		// Polls 21+: 30 seconds (steady state)
		return 30000;
	}
	resetPolling = () => {
		// Reset to fast polling when user navigates to a refresh-enabled page
		this.pollCount = 0;
		if (this.refreshInterval) {
			clearTimeout(this.refreshInterval);
			this.startSmartPolling();
		}
	}
	getParameterByName = (name) => {
		const url = window.location.href;
		name = name.replace(/[[\]]/g, '\\$&');
		let regex = new RegExp(`[?&]${  name  }(=([^&#]*)|&|#|$)`),
			results = regex.exec(url);
		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2].replace(/\+/g, ' '));
	}
	render() {
		let header = "";
		let footer = "";
		if (typeof window !== "undefined" && this.state.ktok) {
			header = <Header app={this} headerName={this.state.headerName} />
			footer = <Footer app={this} lang={this.state.lang} />
		} else {
			header = "";
			footer = "";
		}

		return (
			<div id="app">
	<ToastContainer />
				{header}
				<Content app={this} />
				{footer}
			</div>
		);
	}
}
export default withTranslation()(App)